import { Button, Form, InputNumber, Select, Space, Switch } from "antd";
import { useEffect, useState } from "react";
import AddIcon from "../../../../assets/icons/AddIcon";
import DeleteIcon from "../../../../assets/icons/DeleteIcon";
import CustomModal from "../../../../components/ui/CustomModal";
import Loader from "../../../../components/ui/Loader";
import { employeeTypeList } from "../../../../constants/constants";
import { useGetDepartmentsQuery } from "../../../../features/department/departmentApi";
import { useGetDesignationsQuery } from "../../../../features/designation/designationApi";
import { useGetEmployeesQuery } from "../../../../features/employee/employeeApi";
import {
  useCreateHolidayAllowanceMutation,
  useGetHolidayAllowanceQuery,
  useUpdateOvertimeStatusMutation,
} from "../../../../features/payrollSetting/payrollSettingApi";
import useError from "../../../../hooks/useError";
import { toast } from "../../../../lib/toast";
import { ModalComponentProps } from "../../../../types";
import { selectArrayOption, selectSearchOption } from "../../../../utils/utils";
import { ListComponentType } from "./AdditionsModal";

export default function HolidayAllowanceSettingModal({
  handleClose,
  data: componentItem,
  ...rest
}: ModalComponentProps<any>) {
  const [form] = Form.useForm();
  const { isLoading, isError, error, data } = useGetHolidayAllowanceQuery();
  const [isEnable, setIsEnable] = useState(false);
  const [
    createHolidayAllowance,
    {
      isLoading: isCreateLoading,
      isError: isCreateError,
      error: createError,
      isSuccess: isCreateSuccess,
    },
  ] = useCreateHolidayAllowanceMutation();

  const [
    updateOvertimeStatus,
    {
      isLoading: isStatusLoading,
      isError: isStatusError,
      error: statusError,
      isSuccess: isStatusSuccess,
    },
  ] = useUpdateOvertimeStatusMutation();

  useError(isError, error, "Get holiday allowance setting data fail");
  useError(
    isCreateError,
    createError,
    "Fail to update holiday allowance setting data"
  );
  useError(isStatusError, statusError, "Fail to update status");

  const onFinish = (values: any) => {
    const holiday_allowance = values?.overtime?.map((item: any) => ({
      ...item,
      target: item[`target-${item["effective_for"]}`]?.toString() || null,
      "target-department": undefined,
      "target-designation": undefined,
      "target-employee_type": undefined,
      "target-employee": undefined,
      per_type: "per_day",
    }));

    createHolidayAllowance({ holiday_allowance });
  };

  useEffect(() => {
    if (data?.data?.results?.length) {
      const overtime = data?.data?.results?.map((item) => ({
        id: item?.id,
        fixed_amount: item?.fixed_amount ?? undefined,
        effective_for: item?.effective_for ?? undefined,
        "target-department":
          item?.effective_for === "department"
            ? Number(item?.target)
            : undefined,
        "target-designation":
          item?.effective_for === "designation"
            ? Number(item?.target)
            : undefined,
        "target-employee_type":
          item?.effective_for === "employee_type" ? item?.target : undefined,
        "target-employee":
          item?.effective_for === "employee" ? Number(item?.target) : undefined,
      }));
      form.setFieldsValue({ overtime });
    }
  }, [data?.data?.results, form]);

  const handleChangeStatus = (checked: boolean) => {
    setIsEnable(checked);
    updateOvertimeStatus({
      component_id: componentItem?.id,
      is_enabled: checked,
    });
  };

  useEffect(() => {
    if (isCreateSuccess) {
      toast("success", "Holiday allowance updated successfully");
      handleClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCreateSuccess]);

  useEffect(() => {
    if (isStatusSuccess) {
      toast("success", "Status updated successfully");
    }
  }, [isStatusSuccess]);

  useEffect(() => {
    if (componentItem) {
      setIsEnable(componentItem?.is_active ? true : false);
    }
  }, [componentItem]);

  return (
    <CustomModal
      {...rest}
      title="Holiday Allowance Setting"
      handleClose={handleClose}
      maskClosable={false}
      footer={null}
      width={1100}
    >
      <div className={`flex items-center gap-2 my-4`}>
        <Switch
          checked={isEnable}
          onChange={handleChangeStatus}
          disabled={isStatusLoading}
        />

        <div className="text-header text-sm font-medium">
          <label className="cursor-pointer select-none">
            {isEnable ? "Enabled " : "Disabled "}
          </label>
        </div>
      </div>
      <div className={!isEnable ? "min-h-[100px]" : ""} />

      <div className={!isEnable ? "hidden" : ""}>
        <Form
          className="custom-form"
          layout="vertical"
          requiredMark="optional"
          size="large"
          onFinish={onFinish}
          form={form}
        >
          <div className="flex items-center justify-end mb-2">
            {/* <h3 className="text-header text-[22px] font-semibold">
              Tax Setting
            </h3> */}
            <label
              htmlFor="add-unpaid-leave"
              className="outline-button-primary border flex items-center py-1.5 px-2 cursor-pointer gap-1 rounded"
            >
              <AddIcon />
              <span>Add More</span>
            </label>
          </div>

          {isLoading ? (
            <Loader />
          ) : (
            <>
              <div>
                <Form.List initialValue={[undefined]} name="overtime">
                  {(fields, { add, remove }) => {
                    return (
                      <>
                        <button
                          className="sr-only"
                          id="add-unpaid-leave"
                          onClick={() => add()}
                          type="button"
                        >
                          Add
                        </button>
                        {fields?.map((field, index) => (
                          <div
                            className={`flex items-center justify-between flex-wrap pt-2 pb-2 px-3 py-1 bg-[#fbfcfd] border border-light attendance-table ${
                              index === 0 ? "border-t" : "border-t-0"
                            }`}
                            key={field.key}
                          >
                            <div className="sr-only">
                              <Form.Item
                                className="mb-0"
                                name={[index, "id"]}
                              />
                            </div>
                            <div className="flex items-center gap-2.5">
                              <span className="text-display-body text-sm font-medium">
                                Effective For
                              </span>
                              <Form.Item
                                validateFirst
                                name={[index, "effective_for"]}
                                className="mb-0"
                                rules={[
                                  {
                                    required: true,
                                    message: "Please select",
                                  },
                                ]}
                                initialValue={"global"}
                              >
                                <Select
                                  className="w-full"
                                  options={effectiveForList}
                                  style={{ width: 150 }}
                                  popupMatchSelectWidth={180}
                                  size="small"
                                  placeholder="Select"
                                />
                              </Form.Item>
                              <EffectiveFor index={index} form={form} />

                              <span className="text-display-body text-sm font-medium">
                                Amount
                              </span>

                              <Form.Item
                                validateFirst
                                name={[index, "fixed_amount"]}
                                className="mb-0"
                                rules={[
                                  {
                                    required: true,
                                    message: "Please enter amount",
                                  },
                                  {
                                    type: "number",
                                    min: 0,
                                    message: "Amount cannot be negative",
                                  },
                                  {
                                    validator: async (
                                      _: any,
                                      value: string
                                    ) => {
                                      if (!value) {
                                        return Promise.resolve();
                                      }

                                      if (value?.toString().length > 9) {
                                        return Promise.reject(
                                          new Error(
                                            "Amount cannot exceed 9 digits"
                                          )
                                        );
                                      }
                                    },
                                  },
                                ]}
                              >
                                <InputNumber
                                  placeholder="0.00"
                                  type="number"
                                  prefix="BDT"
                                  onKeyDown={(e) => {
                                    if (
                                      e.key === "ArrowUp" ||
                                      e.key === "ArrowDown"
                                    ) {
                                      e.preventDefault();
                                    }
                                  }}
                                  onWheel={(e) => {
                                    e.preventDefault();
                                    (e.target as HTMLElement).blur();
                                  }}
                                  keyboard={false}
                                  controls={false}
                                  style={{ width: 130 }}
                                  size="small"
                                />
                              </Form.Item>
                              <span className="text-display-body text-sm font-medium">
                                (Per Day)
                              </span>
                            </div>
                            <Button
                              className="action-button"
                              size="small"
                              icon={<DeleteIcon />}
                              onClick={() => {
                                remove(field.name);
                              }}
                            />
                          </div>
                        ))}
                      </>
                    );
                  }}
                </Form.List>
              </div>
              <Space className="w-full pt-6 justify-end" size={12}>
                {/* <Button
            className="normal-dark-button modal-button"
            onClick={handleClose}
            size="middle"
          >
            Cancel
          </Button> */}

                {/* {isEnable && ( */}
                <Button
                  className="modal-button"
                  type="primary"
                  htmlType="submit"
                  size="middle"
                  loading={isCreateLoading}
                >
                  Save
                </Button>
                {/* )} */}
              </Space>
            </>
          )}
        </Form>
      </div>
    </CustomModal>
  );
}

const EffectiveFor = ({ index, form }: ListComponentType) => {
  const effective = Form.useWatch(["overtime", index, "effective_for"], form);
  const {
    isLoading: isEmployeeLoading,
    isError: isEmployeeError,
    error: employeeError,
    data: employeeData,
  } = useGetEmployeesQuery({
    params: {
      status: "active",
      skip: 0,
      limit: 0,
    },
  });
  const {
    isLoading: isDepartmentLoading,
    isError: isDepartmentError,
    error: departmentError,
    data: departmentData,
  } = useGetDepartmentsQuery({
    params: { skip: 0, limit: 0, is_published: 1 },
  });
  const {
    isLoading: isDesignationLoading,
    data: designationData,
    isError: isDesignationError,
    error: designationError,
  } = useGetDesignationsQuery({
    params: {
      limit: 0,
      skip: 0,
      is_published: 1,
    },
  });
  useError(isEmployeeError, employeeError, "Get employee data fail");
  useError(isDepartmentError, departmentError, "Get department data fail");
  useError(isDesignationError, designationError, "Get designation fail");

  return effective === "employee_type" ? (
    <>
      <span className="text-display-body text-sm font-medium">Target</span>

      <Form.Item
        validateFirst
        name={[index, "target-employee_type"]}
        className="mb-0"
        rules={[
          {
            required: true,
            message: "Please select",
          },
        ]}
      >
        <Select
          className="w-full"
          // suffixIcon={<SelectDropdownLgIcon />}
          options={employeeTypeList}
          style={{ width: 140 }}
          popupMatchSelectWidth={180}
          size="small"
          placeholder="Select"
        />
      </Form.Item>
    </>
  ) : effective === "employee" ? (
    <>
      <span className="text-display-body text-sm font-medium">Target</span>

      <Form.Item
        validateFirst
        name={[index, "target-employee"]}
        className="mb-0"
        rules={[
          {
            required: true,
            message: "Please select",
          },
        ]}
      >
        <Select
          className="w-full"
          // suffixIcon={<SelectDropdownLgIcon />}
          options={employeeData?.data?.employees?.map((item) => ({
            label: item?.member?.profile?.name,
            value: item?.id,
          }))}
          loading={isEmployeeLoading}
          style={{ width: 170 }}
          popupMatchSelectWidth={250}
          size="small"
          placeholder="Select"
          filterOption={selectSearchOption}
          showSearch
        />
      </Form.Item>
    </>
  ) : effective === "department" ? (
    <>
      <span className="text-display-body text-sm font-medium">Target</span>

      <Form.Item
        validateFirst
        name={[index, "target-department"]}
        className="mb-0"
        rules={[
          {
            required: true,
            message: "Please select",
          },
        ]}
      >
        <Select
          className="w-full"
          // suffixIcon={<SelectDropdownLgIcon />}
          options={selectArrayOption(
            departmentData?.data?.departments || [],
            "name",
            "id"
          )}
          loading={isDepartmentLoading}
          style={{ width: 140 }}
          popupMatchSelectWidth={180}
          size="small"
          placeholder="Select"
          filterOption={selectSearchOption}
          showSearch
        />
      </Form.Item>
    </>
  ) : effective === "designation" ? (
    <>
      <span className="text-display-body text-sm font-medium ">Target</span>

      <Form.Item
        validateFirst
        name={[index, "target-designation"]}
        className="mb-0"
        rules={[
          {
            required: true,
            message: "Please select",
          },
        ]}
      >
        <Select
          className="w-full"
          // suffixIcon={<SelectDropdownLgIcon />}
          options={
            designationData?.data?.roles?.map((item) => ({
              label: `${item?.name} (${item?.department?.name})`,
              value: item?.id,
            })) || []
          }
          loading={isDesignationLoading}
          style={{ width: 190 }}
          popupMatchSelectWidth={250}
          size="small"
          placeholder="Select"
          filterOption={selectSearchOption}
          showSearch
        />
      </Form.Item>
    </>
  ) : null;
};

const effectiveForList = [
  {
    label: "Global",
    value: "global",
  },
  {
    label: "Employee Type",
    value: "employee_type",
  },
  {
    label: "Employee",
    value: "employee",
  },
  {
    label: "Department",
    value: "department",
  },
  {
    label: "Designation",
    value: "designation",
  },
];
